import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/hooks/useSubscription";
import analytics from "@/lib/segment";
import { Helmet } from "react-helmet-async";
import { PricingCard } from "@/components/pricing/PricingCard";
import { TrustFooter } from "@/components/pricing/TrustFooter";

type PlanTier = "free" | "pro" | "scale" | "fleet";

const SOURCE_DISCOUNT = "car_rental_coach";
const TRIAL_DAYS = 90;

const plansData = [
  {
    tier: "free" as PlanTier,
    name: "Free",
    tagline: "Get Oriented",
    price: "$0",
    priceSubtext: "/ month",
    bestFor: "New or early-stage hosts with 1 car, deciding whether Turo is a good fit.",
    topFeatures: [
      "Free guides for getting to your first listing",
      "Add and track your first vehicle",
      "Log and categorize expenses",
      "Import trips and earnings from Turo",
      "View progress to profitability",
      "Track maintenance timeline",
      "Community support",
    ],
    ctaText: "Sign Up for Free",
    highlighted: false,
    eligibleForPerk: false,
  },
  {
    tier: "pro" as PlanTier,
    name: "Pro",
    tagline: "Know What To Do Next",
    price: "$29",
    priceSubtext: "/ month",
    bestFor: "Hosts with 2–5 vehicles needing organized tracking for their business.",
    topFeatures: [
      "Add and track your vehicles",
      "Log and categorize expenses",
      "Import trips and earnings from Turo",
      "View per-car progress to profitability",
      "Track maintenance timelines",
      "Utilization charts & advanced metrics",
      "Ask Captain AI any question about your fleet",
      "Vehicle-to-vehicle comparisons",
      "Profit improvement insights",
      "Priority email support",
    ],
    ctaText: "Start 3 Months Free",
    highlighted: true,
    eligibleForPerk: true,
  },
  {
    tier: "scale" as PlanTier,
    name: "Scale",
    tagline: "Focus Where It Matters",
    price: "$79",
    priceSubtext: "/ month",
    bestFor: "Growing hosts managing 6-15 vehicles needing operational efficiency.",
    topFeatures: [
      "Add and track your vehicles",
      "Log and categorize expenses",
      "Import trips and earnings from Turo",
      "View per-car progress to profitability",
      "Track maintenance timelines",
      "Utilization charts & advanced metrics",
      "Ask Captain AI any question about your fleet",
      "Vehicle-to-vehicle comparisons",
      "Profit improvement insights",
      "Surface risks in your fleet",
      "Insights on where to focus your attention",
      "Phone & email support",
    ],
    ctaText: "Start 3 Months Free",
    highlighted: false,
    eligibleForPerk: true,
  },
  {
    tier: "fleet" as PlanTier,
    name: "Fleet",
    tagline: "Avoid Risk and Chaos",
    price: "$199",
    priceSubtext: "/ month",
    bestFor: "Professional operators, teams, or large fleets.",
    topFeatures: [
      "Add and track your vehicles",
      "Log and categorize expenses",
      "Import trips and earnings from Turo",
      "View per-car progress to profitability",
      "Track maintenance timelines",
      "Utilization charts & advanced metrics",
      "Ask Captain AI any question about your fleet",
      "Vehicle-to-vehicle comparisons",
      "Profit improvement insights",
      "Surface risks in your fleet",
      "Insights on where to focus your attention",
      "Capital allocation insights",
      "Depreciation curve analysis",
      "Dedicated support specialist",
      "Included setup and training",
      "Priority phone support",
    ],
    ctaText: "Start 3 Months Free",
    highlighted: false,
    eligibleForPerk: true,
  },
];

export default function CarRentalCoach() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { subscriptionTier, isProOrAbove, upgradeSubscription } = useSubscription();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loadingTier, setLoadingTier] = useState<PlanTier | null>(null);

  useEffect(() => {
    const checkoutResult = searchParams.get("checkout");
    if (checkoutResult === "cancelled") {
      toast({
        title: "Checkout cancelled",
        description: "No worries — your CRC perks are still waiting whenever you're ready.",
      });
    }
  }, [searchParams, toast]);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setIsAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handlePlanSelect = async (tier: PlanTier) => {
    const priceMap: Record<PlanTier, number> = {
      free: 0, pro: 29, scale: 79, fleet: 199,
    };
    const price = priceMap[tier];

    analytics.track("Plan Selected", {
      plan_tier: tier,
      plan_price: price,
      billing_interval: "monthly",
      source: "car_rental_coach",
      source_discount: SOURCE_DISCOUNT,
      is_authenticated: isAuthenticated,
    });

    if (tier === "free" || !isAuthenticated) {
      const sourceDiscountParam = tier === "free" ? "" : `&source=${SOURCE_DISCOUNT}`;
      navigate(`/auth?mode=signup&plan=${tier}&billing=monthly${sourceDiscountParam}`);
      return;
    }

    if (isProOrAbove && tier === subscriptionTier) {
      toast({ title: "You're already on this plan" });
      return;
    }

    setLoadingTier(tier);
    try {
      if (isProOrAbove) {
        // Existing paid users — direct plan change (CRC perks only apply to new checkouts)
        await upgradeSubscription(tier as any, "monthly");
        navigate("/app/dashboard?upgrade=success");
      } else {
        const response = await supabase.functions.invoke("create-checkout", {
          body: {
            tier,
            billingInterval: "monthly",
            returnUrl: window.location.origin,
            sourceDiscount: SOURCE_DISCOUNT,
          },
        });

        if (response.error) throw new Error(response.error.message || "Failed to create checkout session");
        if (response.data?.error) throw new Error(response.data.error);

        if (response.data?.url) {
          analytics.track("Checkout Started", {
            plan_tier: tier,
            plan_price: price,
            billing_interval: "monthly",
            source: "car_rental_coach",
            source_discount: SOURCE_DISCOUNT,
          });
          window.location.href = response.data.url;
        } else {
          throw new Error("No checkout URL received");
        }
      }
    } catch (error: any) {
      analytics.track("Error Occurred", {
        error_type: "checkout",
        error_message: error.message,
        plan_tier: tier,
        source_discount: SOURCE_DISCOUNT,
      });
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoadingTier(null);
    }
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Car Rental Coach × HostCaptain</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      <Navbar />

      {/* Friendly insider hero */}
      <section className="bg-gradient-hero text-white py-20">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <div className="inline-block px-3 py-1 mb-6 rounded-full bg-white/15 text-xs font-medium tracking-wide uppercase">
            Members-only · Car Rental Coach
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Welcome Car Rental Coach
          </h1>
          <p className="text-lg text-white/90 mb-4">
            Car Rental Coach members get:
          </p>
          <ul className="text-left text-white/95 max-w-md mx-auto space-y-2 mb-2">
            <li className="flex items-start gap-3">
              <span className="text-xl leading-none">🎁</span>
              <span><strong>3 months free</strong> on any paid plan.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-xl leading-none">💸</span>
              <span><strong>25% off forever</strong> after the 3 free months.</span>
            </li>
          </ul>
          <p className="text-sm text-white/70 mt-6">
            All discounts applied automatically. Please do not share this page outside of Car Rental Coach.
          </p>
        </div>
      </section>

      {/* Pricing Cards Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plansData.filter((p) => p.tier !== "free").map((plan) => (
              <PricingCard
                key={plan.tier}
                name={plan.name}
                tagline={plan.tagline}
                price={plan.price}
                priceSubtext={plan.priceSubtext}
                bestFor={plan.bestFor}
                topFeatures={plan.topFeatures}
                ctaText={plan.ctaText}
                onCtaClick={() => handlePlanSelect(plan.tier)}
                isLoading={loadingTier === plan.tier}
                highlighted={plan.highlighted}
                perkCallout={
                  plan.eligibleForPerk
                    ? "🎁 3 months free, then 25% off forever"
                    : undefined
                }
              />
            ))}
          </div>
        </div>
      </section>

      <TrustFooter showTrustSection={false} />
    </div>
  );
}
